/**
 * @fileoverview ZAMLA Report Page
 * @description Upload a ZAMLA CSV and view crew trading activity analytics.
 *              Failed transactions are fetched with server-side pagination
 *              and filtering for fast rendering.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Stack,
  TextField,
  Tabs,
  Tab,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from '@mui/material';
import {
  Upload,
  BarChart3,
  Users,
  AlertTriangle,
  Activity,
  ArrowUpDown,
  X,
  RefreshCw,
  Search,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { zamlaReportApi } from '../services/zamlaReportApi';
import './ZamlaReport.css';

/* ─── Constants ─── */
const STATUS_COLORS = {
  Success: '#00AF66',
  Failure: '#E31837',
  Implausible: '#fd7e14',
  Plausible: '#0097A9',
};
const STATUS_BADGE = {
  Failure: 'zamla-badge zamla-badge-failure',
  Implausible: 'zamla-badge zamla-badge-implausible',
};
const PAGE_SIZE = 50;
const DEBOUNCE_MS = 400;

/* ─── Main Component ─── */
const ZamlaReport = () => {
  const [reportData, setReportData] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // ── Failed transactions (server-side) ──
  const [failedRows, setFailedRows] = useState([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [failedPagination, setFailedPagination] = useState({
    page: 1, pageSize: PAGE_SIZE, totalPages: 1, totalFiltered: 0, totalUnfiltered: 0,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [colFilters, setColFilters] = useState({});
  const debounceRef = useRef(null);

  /* ─── Upload handler ─── */
  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setReportData(null);
    setUploadId(null);
    setFailedRows([]);
    setGlobalFilter('');
    setColFilters({});

    try {
      const data = await zamlaReportApi.parseCSV(file);
      if (data.error) {
        setError(data.error);
      } else {
        setReportData(data);
        setUploadId(data.uploadId);
        setFailedPagination((p) => ({
          ...p, page: 1, totalUnfiltered: data.failedTransactionsCount || 0,
        }));
      }
    } catch (err) {
      setError(err.message || 'Failed to parse CSV');
    } finally {
      setLoading(false);
    }
  }, []);

  const onFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleReset = () => {
    setReportData(null);
    setUploadId(null);
    setError(null);
    setFailedRows([]);
    setGlobalFilter('');
    setColFilters({});
  };

  /* ─── Fetch failed transactions from backend ─── */
  const fetchFailed = useCallback(async (page = 1) => {
    if (!uploadId) return;
    setFailedLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        search: globalFilter || undefined,
        ...Object.fromEntries(
          Object.entries(colFilters).filter(([, v]) => v)
        ),
      };
      const res = await zamlaReportApi.getFailedTransactions(uploadId, params);
      setFailedRows(res.data || []);
      setFailedPagination(res.pagination || {});
    } catch (err) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setFailedLoading(false);
    }
  }, [uploadId, globalFilter, colFilters]);

  // Auto-fetch when switching to the Failed tab or when filters change
  useEffect(() => {
    if (activeTab === 2 && uploadId) {
      // Debounce filter changes
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchFailed(1), DEBOUNCE_MS);
      return () => clearTimeout(debounceRef.current);
    }
  }, [activeTab, uploadId, globalFilter, colFilters]);

  const handlePageChange = (newPage) => {
    fetchFailed(newPage);
  };

  const handleColFilter = (col, val) => {
    setColFilters((prev) => ({ ...prev, [col]: val }));
  };

  /* ═══════════════════════════════════════════
     RENDER — Upload State
     ═══════════════════════════════════════════ */
  if (!reportData && !loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowUpDown size={20} color="#fff" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#01426A', lineHeight: 1.2 }}>ZAMLA Report</Typography>
            <Typography variant="caption" color="text.secondary">Crew trading &amp; activity analysis</Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <label htmlFor="zamla-csv-upload" style={{ display: 'block', cursor: 'pointer' }}>
            <div className={`zamla-upload-area${dragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)} onDrop={onDrop}
            >
              <Box sx={{ width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2, background: 'linear-gradient(135deg, rgba(0,151,169,0.1) 0%, rgba(1,66,106,0.1) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Upload size={28} color="#0097A9" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A', mb: 0.5 }}>Drop your ZAMLA CSV here</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Semicolon-delimited CSV files exported from ZAMLA</Typography>
              <Button variant="contained" component="span" size="small" sx={{ background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)' }}>Select CSV File</Button>
            </div>
          </label>
          <input id="zamla-csv-upload" type="file" accept=".csv" hidden onChange={onFileSelect} />
        </Paper>
      </Box>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER — Loading
     ═══════════════════════════════════════════ */
  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowUpDown size={20} color="#fff" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#01426A' }}>ZAMLA Report</Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <CircularProgress size={40} sx={{ mb: 2, color: '#0097A9' }} />
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#01426A' }}>Parsing CSV data…</Typography>
          <LinearProgress sx={{ mt: 2, maxWidth: 400, mx: 'auto', '& .MuiLinearProgress-bar': { bgcolor: '#0097A9' } }} />
        </Paper>
      </Box>
    );
  }

  /* ─── Shorthand ─── */
  const { summary, statusStats, actionStats, topCrew, hourlyStats, dailyStats, tradeWindowStats } = reportData;
  const maxAction = actionStats.length > 0 ? Math.max(...actionStats.map((a) => a.count)) : 1;
  const maxHour = Math.max(...hourlyStats.map((h) => h.count), 1);
  const maxDay = dailyStats.length > 0 ? Math.max(...dailyStats.map((d) => d.count)) : 1;
  const failedCount = reportData.failedTransactionsCount || 0;

  /* ═══════════════════════════════════════════
     RENDER — Report
     ═══════════════════════════════════════════ */
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowUpDown size={20} color="#fff" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#01426A', lineHeight: 1.2 }}>ZAMLA Report</Typography>
            <Typography variant="caption" color="text.secondary">
              {reportData.dateRange || 'No date range'} &bull; {reportData.filename}
            </Typography>
          </Box>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshCw size={14} />} onClick={handleReset}
          sx={{ borderColor: '#cbd5e1', color: '#64748b', '&:hover': { borderColor: '#01426A', color: '#01426A' } }}
        >New Upload</Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }} elevation={0} variant="outlined">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', py: 1, minHeight: 42 },
            '& .Mui-selected': { color: '#01426A' },
            '& .MuiTabs-indicator': { bgcolor: '#0097A9', height: 3 },
          }}
        >
          <Tab label="Overview" icon={<BarChart3 size={16} />} iconPosition="start" />
          <Tab label="Crew Activity" icon={<Users size={16} />} iconPosition="start" />
          <Tab label={`All Transactions (${failedCount})`} icon={<Activity size={16} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ═══════════════ TAB 0 — Overview ═══════════════ */}
      {activeTab === 0 && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            {[
              { value: summary.totalRecords, label: 'Total Transactions', icon: <Activity size={18} />, color: '#01426A' },
              { value: summary.uniqueCrew, label: 'Unique Crew', icon: <Users size={18} />, color: '#0097A9' },
              { value: summary.actionTypes, label: 'Action Types', icon: <TrendingUp size={18} />, color: '#00AF66' },
              { value: summary.missingCrewId, label: 'Missing Crew ID', icon: <AlertTriangle size={18} />, color: '#fd7e14' },
            ].map((card) => (
              <Paper key={card.label} variant="outlined" sx={{ p: 2.5, borderRadius: 2, textAlign: 'center' }}>
                <Box sx={{ mb: 1, color: card.color }}>{card.icon}</Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>{card.value.toLocaleString()}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{card.label}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Status Breakdown */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#01426A', mb: 1.5, pb: 1, borderBottom: '2px solid #f0f2f5' }}>
              Transaction Status
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5 }}>
              {statusStats.map((s) => {
                const color = STATUS_COLORS[s.name] || '#6c757d';
                const pct = ((s.count / summary.totalRecords) * 100).toFixed(1);
                return (
                  <Paper key={s.name} elevation={0}
                    sx={{ p: 2, textAlign: 'center', borderLeft: `4px solid ${color}`, bgcolor: '#fafbfc', borderRadius: 1 }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.name}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color, my: 0.5 }}>{s.count.toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.disabled">{pct}%</Typography>
                  </Paper>
                );
              })}
            </Box>
          </Paper>

          {/* Hourly + Daily Charts */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#01426A', mb: 1.5, pb: 1, borderBottom: '2px solid #f0f2f5' }}>
                Hourly Distribution (UTC)
              </Typography>
              <div className="zamla-chart-container">
                {hourlyStats.map((h) => (
                  <Tooltip key={h.hour} title={`${String(h.hour).padStart(2, '0')}:00 — ${h.count.toLocaleString()}`} arrow>
                    <div className="zamla-bar-wrapper">
                      <div className="zamla-bar zamla-hourly-bar" style={{ height: `${Math.round((h.count / maxHour) * 100)}%` }} />
                      <div className="zamla-bar-label">{String(h.hour).padStart(2, '0')}</div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#01426A', mb: 1.5, pb: 1, borderBottom: '2px solid #f0f2f5' }}>
                Daily Distribution
              </Typography>
              <div className="zamla-chart-container">
                {dailyStats.map((d) => (
                  <Tooltip key={d.date} title={`${d.date} — ${d.count.toLocaleString()}`} arrow>
                    <div className="zamla-bar-wrapper">
                      <div className="zamla-bar zamla-daily-bar" style={{ height: `${Math.round((d.count / maxDay) * 100)}%` }} />
                      <div className="zamla-bar-label">{d.date.slice(5)}</div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </Paper>
          </Box>

          {/* Actions Breakdown */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#01426A', mb: 1.5, pb: 1, borderBottom: '2px solid #f0f2f5' }}>
              Actions Breakdown
            </Typography>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {actionStats.map((a) => (
                  <tr key={a.name} style={{ borderBottom: '1px solid #f0f2f5' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500, minWidth: 200, fontSize: '0.875rem' }}>{a.name}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#01426A', minWidth: 80, fontSize: '0.875rem' }}>{a.count.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px', width: '50%' }}>
                      <div className="zamla-action-bar" style={{ width: `${Math.round((a.count / maxAction) * 100)}%` }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>

          {/* Trade Window Types */}
          {tradeWindowStats.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#01426A', mb: 1.5, pb: 1, borderBottom: '2px solid #f0f2f5' }}>
                Trade Window Types
              </Typography>
              <table className="zamla-crew-table">
                <thead><tr><th>Type</th><th style={{ textAlign: 'right' }}>Count</th></tr></thead>
                <tbody>
                  {tradeWindowStats.map((tw) => (
                    <tr key={tw.name}><td>{tw.name}</td><td className="crew-count">{tw.count.toLocaleString()}</td></tr>
                  ))}
                </tbody>
              </table>
            </Paper>
          )}
        </>
      )}

      {/* ═══════════════ TAB 1 — Crew Activity ═══════════════ */}
      {activeTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#01426A', mb: 1.5, pb: 1, borderBottom: '2px solid #f0f2f5' }}>
            Top {topCrew.length} Most Active Crew Members
          </Typography>
          <table className="zamla-crew-table">
            <thead>
              <tr>
                <th>#</th><th>Crew ID</th><th>Current Homebase</th><th>Next Month Homebase</th>
                <th style={{ textAlign: 'right' }}>Transactions</th>
              </tr>
            </thead>
            <tbody>
              {topCrew.map((c, i) => (
                <tr key={c.crewId}>
                  <td className="rank">{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{c.crewId}</td>
                  <td style={{ color: '#666', fontStyle: 'italic' }}>{c.currentHomebase || '—'}</td>
                  <td style={{ color: '#666', fontStyle: 'italic' }}>
                    {c.nextHomebase || '—'}
                    {c.currentHomebase && c.nextHomebase && c.currentHomebase !== c.nextHomebase && (
                      <Chip label="changing" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} />
                    )}
                  </td>
                  <td className="crew-count">{c.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Paper>
      )}

      {/* ═══════════════ TAB 2 — Failed & Implausible ═══════════════ */}
      {activeTab === 2 && (() => {
        const COLS = [
          { key: '_slNo',           label: '#',                w: 44,  align: 'center', noFilter: true },
          { key: 'timestamp',       label: 'Timestamp',        w: 175 },
          { key: 'crew',            label: 'Crew ID',           w: 85 },
          { key: 'crewType',        label: 'Crew Type',         w: 92, select: true, opts: ['CA','FO','FAL'] },
          { key: 'currentHomebase', label: 'Homebase (Cur.)',   w: 105 },
          { key: 'nextHomebase',    label: 'Homebase (Next)',   w: 105 },
          { key: 'action',          label: 'Action',            w: 100 },
          { key: 'status',          label: 'Status',            w: 108, select: true, opts: ['Success','Failure','Implausible','Plausible'] },
          { key: 'activitiesName',  label: 'Activity' },
          { key: 'otherCrew',       label: 'Other Crew',        w: 88 },
          { key: 'reason',          label: 'Reason' },
        ];

        return (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* ── Toolbar ── */}
            <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', bgcolor: '#fff', borderBottom: '1px solid #e5e7eb' }}>
              <ArrowUpDown size={15} color="#01426A" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                All Transactions
              </Typography>
              <Chip size="small" variant="outlined"
                label={`${failedPagination.totalFiltered ?? failedCount} / ${failedPagination.totalUnfiltered ?? failedCount}`}
                sx={{ fontWeight: 600, fontSize: '0.7rem', height: 21, borderColor: '#d1d5db', color: '#6b7280' }}
              />
              {failedLoading && <CircularProgress size={14} thickness={5} sx={{ color: '#0097A9' }} />}
              <Box sx={{ flex: 1 }} />
              <TextField size="small" placeholder="Search all columns…"
                value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} color="#9ca3af" /></InputAdornment> }}
                sx={{ width: 220, '& .MuiOutlinedInput-root': { height: 30, borderRadius: '6px', fontSize: '0.8rem' } }}
              />
              {(globalFilter || Object.values(colFilters).some(Boolean)) && (
                <Button size="small" onClick={() => { setGlobalFilter(''); setColFilters({}); }}
                  sx={{ minWidth: 'auto', color: '#b91c1c', textTransform: 'none', fontWeight: 600, fontSize: '0.76rem' }}
                ><X size={13} style={{ marginRight: 2 }} />Clear</Button>
              )}
            </Box>

            {/* ── Data grid ── */}
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" sx={{ minWidth: 1140 }}>
                <colgroup>
                  {COLS.map((c) => <col key={c.key} style={c.w ? { width: c.w } : undefined} />)}
                </colgroup>

                {/* Header: label + filter in one row */}
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f9fafb', borderBottom: '2px solid #e5e7eb', p: '8px 10px', verticalAlign: 'top', position: 'sticky', top: 0, zIndex: 2 } }}>
                    {COLS.map((c) => (
                      <TableCell key={c.key} align={c.align || 'left'}>
                        <Box sx={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#374151', lineHeight: 1.2, whiteSpace: 'nowrap', mb: c.noFilter ? 0 : 0.75 }}>
                          {c.label}
                        </Box>
                        {!c.noFilter && (
                          c.select ? (
                            <select value={colFilters[c.key] || ''} onChange={(e) => handleColFilter(c.key, e.target.value)}
                              aria-label={`Filter by ${c.label}`}
                              style={{ width: '100%', height: 24, fontSize: '0.74rem', border: '1px solid #d1d5db', borderRadius: 4, padding: '0 4px', background: '#fff', color: '#374151', outline: 'none' }}
                            >
                              <option value="">All</option>
                              {c.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input value={colFilters[c.key] || ''} onChange={(e) => handleColFilter(c.key, e.target.value)}
                              placeholder="Filter…"
                              aria-label={`Filter by ${c.label}`}
                              style={{ width: '100%', height: 24, fontSize: '0.74rem', border: '1px solid #d1d5db', borderRadius: 4, padding: '0 6px', background: '#fff', color: '#374151', outline: 'none', boxSizing: 'border-box' }}
                              onFocus={(e) => { e.target.style.borderColor = '#0097A9'; }}
                              onBlur={(e) => { e.target.style.borderColor = '#d1d5db'; }}
                            />
                          )
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {/* Body */}
                <TableBody>
                  {failedLoading && failedRows.length === 0 ? (
                    <TableRow><TableCell colSpan={COLS.length} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} sx={{ color: '#0097A9' }} />
                    </TableCell></TableRow>
                  ) : failedRows.length === 0 ? (
                    <TableRow><TableCell colSpan={COLS.length} align="center" sx={{ py: 6, color: '#9ca3af' }}>
                      No matching records
                    </TableCell></TableRow>
                  ) : failedRows.map((f, idx) => {
                    const n = (failedPagination.page - 1) * failedPagination.pageSize + idx + 1;
                    const stripe = idx % 2 === 0 ? '#fff' : '#f9fafb';
                    return (
                      <TableRow key={idx} sx={{ bgcolor: stripe, opacity: failedLoading ? 0.4 : 1, '&:hover': { bgcolor: '#eff6ff' }, '& td': { py: '7px', px: '10px', fontSize: '0.8rem', borderBottom: '1px solid #f3f4f6' } }}>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.72rem' }}>{n}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{f.timestamp}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#111827' }}>{f.crew}</TableCell>
                        <TableCell sx={{ color: '#374151', fontWeight: 500 }}>{f.crewType || '—'}</TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>{f.currentHomebase || '—'}</TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>
                          {f.nextHomebase || '—'}
                          {f.currentHomebase && f.nextHomebase && f.currentHomebase !== f.nextHomebase && (
                            <Tooltip title="Homebase changing next month" arrow>
                              <span style={{ marginLeft: 4, cursor: 'help' }}>⚠️</span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{f.action}</TableCell>
                        <TableCell>
                          <Box component="span" sx={{
                            display: 'inline-block', px: 1, py: '2px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600,
                            ...(f.status === 'Failure'
                              ? { bgcolor: '#fef2f2', color: '#b91c1c' }
                              : f.status === 'Success'
                              ? { bgcolor: '#ecfdf5', color: '#047857' }
                              : f.status === 'Plausible'
                              ? { bgcolor: '#ecfeff', color: '#0e7490' }
                              : { bgcolor: '#fffbeb', color: '#b45309' }),
                          }}>{f.status}</Box>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={`${f.activitiesName || ''}${f.activitiesSt ? ` (${f.activitiesSt})` : ''}`} arrow disableHoverListener={!f.activitiesName}>
                            <span>{f.activitiesName || '—'}{f.activitiesSt ? ` (${f.activitiesSt})` : ''}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{f.otherCrew || '—'}</TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={f.reason || ''} arrow disableHoverListener={!f.reason}>
                            <span>{f.reason || '—'}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Pagination ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: '1px solid #e5e7eb', bgcolor: '#f9fafb' }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.76rem' }}>
                {failedRows.length > 0
                  ? `${(failedPagination.page - 1) * failedPagination.pageSize + 1}–${Math.min(failedPagination.page * failedPagination.pageSize, failedPagination.totalFiltered)} of ${failedPagination.totalFiltered}`
                  : `${failedPagination.totalFiltered} results`}
              </Typography>
              {failedPagination.totalPages > 1 && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <IconButton size="small" disabled={failedPagination.page <= 1 || failedLoading}
                    onClick={() => handlePageChange(1)} sx={{ width: 26, height: 26 }}
                  ><ChevronLeft size={11} /><ChevronLeft size={11} style={{ marginLeft: -7 }} /></IconButton>
                  <IconButton size="small" disabled={failedPagination.page <= 1 || failedLoading}
                    onClick={() => handlePageChange(failedPagination.page - 1)} sx={{ width: 26, height: 26 }}
                  ><ChevronLeft size={13} /></IconButton>
                  {Array.from({ length: Math.min(7, failedPagination.totalPages) }, (_, i) => {
                    const s = Math.max(1, Math.min(failedPagination.page - 3, failedPagination.totalPages - 6));
                    const pg = s + i;
                    if (pg > failedPagination.totalPages) return null;
                    const on = pg === failedPagination.page;
                    return (
                      <Button key={pg} size="small" disabled={failedLoading} onClick={() => handlePageChange(pg)}
                        sx={{ minWidth: 28, height: 26, fontSize: '0.73rem', p: 0, borderRadius: 1, fontWeight: on ? 700 : 400,
                          bgcolor: on ? '#111827' : 'transparent', color: on ? '#fff' : '#6b7280',
                          '&:hover': { bgcolor: on ? '#1f2937' : '#f3f4f6' },
                        }}
                      >{pg}</Button>
                    );
                  })}
                  <IconButton size="small" disabled={failedPagination.page >= failedPagination.totalPages || failedLoading}
                    onClick={() => handlePageChange(failedPagination.page + 1)} sx={{ width: 26, height: 26 }}
                  ><ChevronRight size={13} /></IconButton>
                  <IconButton size="small" disabled={failedPagination.page >= failedPagination.totalPages || failedLoading}
                    onClick={() => handlePageChange(failedPagination.totalPages)} sx={{ width: 26, height: 26 }}
                  ><ChevronRight size={11} /><ChevronRight size={11} style={{ marginLeft: -7 }} /></IconButton>
                </Stack>
              )}
            </Box>
          </Paper>
        );
      })()}
    </Box>
  );
};

export default ZamlaReport;
